import { HStack, Stack } from "@chakra-ui/react";
import { useMetricsInterval, useTimezone } from "@tether-web-portal/hooks";
import { TimestampRange } from "@tether-web-portal/types/TimestampRange";
import { DateTime } from "luxon";
import { useMemo } from "react";
import dayjs from "@tether-web-portal/dayjs-setup";
import {
  defaultTimeButtons,
  TimeButtonOptions,
  TimeRangeButtons,
} from "@tether-web-portal/components/button/TimeRangeButtons";
import { DateRangePicker } from "@tether-web-portal/components/datepicker/DateRangePicker";
import { ReadingResolution } from "@tether-web-portal/api/readings/hooks";
import { useDateRangeControls } from "@tether-web-portal/components/property/useDateRangeControls";

interface DateRangeControlsProps {
  buttonOptions?: TimeButtonOptions[];
  maxSelectedDays?: number;
}

export interface DateRangeResolution {
  powerResolution: ReadingResolution;
  environmentalResolution: ReadingResolution;
}

export const getResolutionDaysForRange = (dateRange: TimestampRange): DateRangeResolution => {
  if (!dateRange.toTimestamp || !dateRange.fromTimestamp) {
    return {
      powerResolution: "HOURLY",
      environmentalResolution: "HOURLY",
    };
  }

  const days = Math.floor(
    DateTime.fromISO(dateRange.toTimestamp, { zone: "utc" })
      .diff(DateTime.fromISO(dateRange.fromTimestamp, { zone: "utc" }))
      .as("days"),
  );

  let powerResolution: ReadingResolution = "HOURLY";

  // power

  if (days > 25) {
    powerResolution = "DAILY";
  } else if (days >= 2) {
    powerResolution = "HOURLY";
  } else {
    powerResolution = "FIFTEENMINUTE";
  }

  // environmental
  const environmentalResolution: ReadingResolution = days > 4 ? "HOURLY" : "FIFTEENMINUTE";
  return {
    environmentalResolution,
    powerResolution,
  };
};

export const maxReadingDays = 366;

export const DateRangeControls = ({
  buttonOptions = defaultTimeButtons,
  maxSelectedDays: maxSelectedDaysProp = maxReadingDays,
}: DateRangeControlsProps) => {
  const { setCustomMetricsInterval } = useMetricsInterval();

  const timezone = useTimezone();

  const { selectedButton, setNewTimeRangeFromButton, customDateRange, onDatesChange } =
    useDateRangeControls({ buttonOptions, timezone });

  const maxSelectedDays = useMemo(
    () => (maxSelectedDaysProp === -1 ? undefined : maxSelectedDaysProp),
    [maxSelectedDaysProp],
  );

  const maxBookingDate = useMemo(() => dayjs().tz(timezone).endOf("day"), [timezone]);

  return (
    <HStack justifyContent={"flex-end"}>
      <HStack>
        <Stack bg={"white"} p={1} border="1px" borderColor="gray.200" borderRadius="md">
          <TimeRangeButtons
            onSelect={setNewTimeRangeFromButton}
            overridden={selectedButton === null}
            selectedValue={selectedButton}
            buttons={buttonOptions}
          />
        </Stack>
        <DateRangePicker
          startDate={customDateRange.startDate}
          endDate={customDateRange.endDate}
          onDatesChange={onDatesChange}
          bg={selectedButton === null ? "black" : "white"}
          color={selectedButton === null ? "white" : undefined}
          borderColor="gray.100"
          borderRadius={6}
          maxBookingDate={maxBookingDate}
          horizontalOffset={-460}
          maxSelectedDays={maxSelectedDays}
          showDateRangeOnButton={selectedButton === null}
          onClick={() => {
            // If the custom date button is clicked and a range has already been specified there, select it
            if (customDateRange.startDate && customDateRange.endDate) {
              setCustomMetricsInterval(customDateRange.startDate, customDateRange.endDate);
            }
          }}
        />
      </HStack>
    </HStack>
  );
};

export default DateRangeControls;
